import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileTextIcon, PlusIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { Button } from '../../../ui/Button';

export interface NoAgreementCalloutProps {
  /** Display name of the client this callout is rendered against. */
  clientName: string;
  /** Fires when the user clicks the primary "Create agreement" button. */
  onCreate: () => void;
  className?: string;
}

/**
 * Soft empty-state callout shown above the pricing sheet when the
 * client has no open agreement (`status ∈ {new, active}`) yet.
 *
 * Purely presentational — the parent owns the create-agreement
 * drawer lifecycle and the `defaults` / `lockedFields` it forwards
 * to `AgreementEditDrawer`. This component just emits `onCreate`.
 */
export function NoAgreementCallout({
  clientName,
  onCreate,
  className,
}: NoAgreementCalloutProps) {
  const { t } = useTranslation(['m-agreement']);

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border border-dashed border-gray-200 bg-gray-50/60 p-4',
        className,
      )}>
      <div className="flex size-9 items-center justify-center rounded-full bg-primary/10 text-primary flex-shrink-0">
        <FileTextIcon className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-gray-900">
          {t('callout.no_agreement.title', {
            defaultValue: 'No agreement with {{name}} yet',
            name: clientName,
          })}
        </div>
        <p className="mt-0.5 text-xs text-gray-500">
          {t('callout.no_agreement.description', {
            defaultValue:
              'Lock terms, payment, and expected volumes so the rates below sit under a contract envelope both sides agree to.',
          })}
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        onClick={onCreate}
        className="gap-1.5 flex-shrink-0">
        <PlusIcon className="size-3.5" />
        {t('callout.no_agreement.cta', {
          defaultValue: 'Create agreement',
        })}
      </Button>
    </div>
  );
}
